/**
 * A user-agent string, said out loud.
 *
 * The devices list printed the raw string, truncated mid-token, so the row read
 * `Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTM…`.
 * Nobody audits their own sessions from that. "Chrome on macOS" is what the
 * question actually was.
 *
 * A lookup, not a dependency. Full UA parsing is a maintained database of
 * thousands of strings and this list needs to cover the browsers a finance back
 * office actually uses — and when it does not match, it says so instead of
 * guessing, and the original string is still one hover away.
 *
 * Order matters in both lists. Every Chromium browser claims to be Chrome and
 * Safari; Edge and Opera add their own token afterwards, so the more specific
 * name has to be tested first or everything reads as Chrome. Same on the other
 * side: Android carries "Linux" in its string, and iPadOS 13 and later claim to
 * be a Macintosh unless "Mobile" gives them away.
 */
const BROWSERS = [
  [/\bEdgA?\/|\bEdge\//i, "Edge"],
  [/\bOPR\/|\bOpera\//i, "Opera"],
  [/\bVivaldi\//i, "Vivaldi"],
  [/\bBrave\//i, "Brave"],
  [/\bSamsungBrowser\//i, "Samsung Internet"],
  [/\bFirefox\/|\bFxiOS\//i, "Firefox"],
  // Before Chrome, and not matched by it: the `\b` in `\bChrome/` does not fire
  // inside "HeadlessChrome", so without this line a headless browser reports
  // itself as Safari — which is how our own screenshots came to say so.
  [/HeadlessChrome\//i, "Chrome (headless)"],
  [/\bCriOS\//i, "Chrome"],
  [/\bChrome\//i, "Chrome"],
  [/\bSafari\//i, "Safari"],
]

const PLATFORMS = [
  [/\bAndroid\b/i, "Android"],
  [/\b(iPhone|iPod)\b/i, "iOS"],
  [/\biPad\b/i, "iPadOS"],
  [/\bWindows NT\b/i, "Windows"],
  [/\b(Macintosh|Mac OS X)\b/i, "macOS"],
  [/\bCrOS\b/i, "ChromeOS"],
  [/\bLinux\b/i, "Linux"],
]

function firstMatch(table, value) {
  for (const [pattern, label] of table) {
    if (pattern.test(value)) return label
  }
  return null
}

/**
 * `{ label, raw }` — the label for the row, the raw string for the tooltip.
 *
 * Support asks for the exact string often enough that dropping it would trade
 * one bad row for one missing answer, so it is never thrown away.
 */
export function describeUserAgent(value) {
  const raw = String(value || "").trim()
  if (!raw) return { label: "Unknown device", raw: "" }

  const browser = firstMatch(BROWSERS, raw)
  let platform = firstMatch(PLATFORMS, raw)

  // iPadOS 13 and later report themselves as a Macintosh. "Mobile" in a
  // Safari string on a Mac is the giveaway — a desktop Mac never sends it.
  if (platform === "macOS" && /\bMobile\b/i.test(raw)) platform = "iPadOS"

  if (browser && platform) return { label: `${browser} on ${platform}`, raw }
  if (browser) return { label: browser, raw }
  if (platform) return { label: platform, raw }
  return { label: "Unknown device", raw }
}
